import type { 
  ApiResponse, 
  User, 
  License, 
  SavedQuery, 
  QueryFilters, 
  DataQueryResponse,
  SyncLog,
  AuditLog,
  SystemStats
} from '@/types';
import { logger } from './logger';
import { sanitizer } from './sanitizer';

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string = '') {
    this.baseUrl = baseUrl;
  }

  private async request<T = any>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseUrl}${endpoint}`;
    
    // Note: JWT tokens now handled via httpOnly cookies
    // No client-side token access needed for security
    logger.apiRequest(options.method || 'GET', endpoint);
    
    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      credentials: 'include', // Include httpOnly cookies automatically
      ...options,
    };

    try {
      const response = await fetch(url, config);
      
      logger.apiResponse(endpoint, response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        logger.error(`API request failed: ${response.status}`, { 
          endpoint, 
          status: response.status,
          statusText: response.statusText 
        });
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      logger.error(`API request failed for ${endpoint}`, error);
      throw error;
    }
  }

  // Auth endpoints
  async getCurrentUser(): Promise<ApiResponse<User>> {
    return this.request<User>('/auth/me');
  }

  async logout(): Promise<ApiResponse> {
    return this.request('/auth/logout', { 
      method: 'POST',
      headers: {} // Remove Content-Type header for logout since we send no body
    });
  }

  async getAuthStatus(): Promise<ApiResponse<{ authenticated: boolean; user?: User }>> {
    return this.request('/auth/status');
  }

  // Data endpoints
  async queryData(filters: QueryFilters): Promise<DataQueryResponse> {
    // Sanitize filters before sending to API
    const sanitizedFilters = sanitizer.sanitizeFilters(filters);
    logger.debug('Querying data with sanitized filters', { filterCount: Object.keys(sanitizedFilters).length });
    const response = await this.request('/api/data/query', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(sanitizedFilters),
    });
    
    // Backend returns { success: true, data: { licenses, total, ... } }
    // Extract the data portion to match DataQueryResponse interface
    if (response.success && response.data) {
      return response.data as DataQueryResponse;
    } else {
      throw new Error(response.error || 'Failed to query data');
    }
  }

  // Saved queries endpoints
  async getSavedQueries(): Promise<ApiResponse<SavedQuery[]>> {
    return this.request<SavedQuery[]>('/api/queries');
  }

  async createSavedQuery(query: Omit<SavedQuery, 'id' | 'user_id' | 'created_at' | 'updated_at'>): Promise<ApiResponse<SavedQuery>> {
    return this.request<SavedQuery>('/api/queries', {
      method: 'POST',
      body: JSON.stringify(query),
    });
  }

  async updateSavedQuery(id: string, query: Partial<SavedQuery>): Promise<ApiResponse<SavedQuery>> {
    return this.request<SavedQuery>(`/api/queries/${id}`, {
      method: 'PUT',
      body: JSON.stringify(query),
    });
  }

  async deleteSavedQuery(id: string): Promise<ApiResponse> {
    return this.request(`/api/queries/${id}`, {
      method: 'DELETE',
    });
  }

  // Admin endpoints
  async triggerDataSync(): Promise<ApiResponse> {
    return this.request('/api/admin/sync-abc-data', {
      method: 'POST',
    });
  }

  async triggerEnrichment(options?: {
    batchSize?: number;
    licenseIds?: string[];
  }): Promise<ApiResponse> {
    return this.request('/api/admin/enrich-stale-data', {
      method: 'POST',
      body: JSON.stringify(options || {}),
    });
  }

  async removeChainStores(): Promise<ApiResponse> {
    return this.request('/api/admin/chains', {
      method: 'DELETE',
    });
  }

  async getSystemStats(): Promise<ApiResponse<SystemStats>> {
    return this.request<SystemStats>('/api/admin/stats');
  }

  async getSyncLogs(limit: number = 50): Promise<ApiResponse<SyncLog[]>> {
    return this.request<SyncLog[]>(`/api/admin/sync-logs?limit=${limit}`);
  }

  async getAuditLogs(limit: number = 50): Promise<ApiResponse<AuditLog[]>> {
    return this.request<AuditLog[]>(`/api/admin/audit-logs?limit=${limit}`);
  }

  // Health endpoints
  async getHealthStatus(): Promise<ApiResponse> {
    return this.request('/health');
  }

  async getDetailedHealth(): Promise<ApiResponse> {
    return this.request('/health/detailed');
  }

  // Chain management endpoints
  async getChainPatterns(): Promise<ApiResponse<any[]>> {
    return this.request('/api/chains/patterns');
  }

  async createChainPattern(pattern: {
    name: string;
    business_name_pattern?: string;
    licensee_name_pattern?: string;
    pattern_type: string;
    category: string;
  }): Promise<ApiResponse<any>> {
    return this.request('/api/chains/patterns', {
      method: 'POST',
      body: JSON.stringify(pattern)
    });
  }

  async markLicenseAsChain(licenseId: string, isChain: boolean): Promise<ApiResponse<any>> {
    return this.request('/api/chains/mark-chain', {
      method: 'POST',
      body: JSON.stringify({ license_id: licenseId, is_chain: isChain })
    });
  }

  async bulkMarkChain(patterns: {
    business_name_pattern?: string;
    licensee_name_pattern?: string;
    is_chain: boolean;
  }): Promise<ApiResponse<any>> {
    return this.request('/api/chains/bulk-mark-chain', {
      method: 'POST',
      body: JSON.stringify(patterns)
    });
  }

  async getChainStats(): Promise<ApiResponse<any>> {
    return this.request('/api/chains/stats');
  }

  async triggerFilteredEnrichment(filters: QueryFilters): Promise<ApiResponse<{ jobId: string }>> {
    const sanitizedFilters = sanitizer.sanitizeFilters(filters);
    logger.debug('Triggering filtered enrichment', { filterCount: Object.keys(sanitizedFilters).length });
    
    const result = await this.request<{ jobId: string }>('/api/admin/enrich-filtered-results', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ filters: sanitizedFilters }),
    });
    logger.debug('Enrichment job triggered', { jobId: result.data?.jobId });
    return result;
  }

  async exportFilteredCSV(filters: QueryFilters, selectedFields?: string[]): Promise<Blob> {
    console.log('📡 exportFilteredCSV called with filters:', filters);
    console.log('📡 selectedFields:', selectedFields);
    
    const requestBody = {
      ...filters,
      ...(selectedFields && { selectedFields })
    };
    
    const response = await fetch(`${this.baseUrl}/api/data/export-csv`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Include httpOnly cookies automatically
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      throw new Error(`CSV export failed: ${response.statusText}`);
    }

    console.log('📨 CSV export successful, returning blob');
    return response.blob();
  }

  async getExportFields(): Promise<ApiResponse<any>> {
    logger.debug('Fetching export fields metadata');
    
    const response = await this.request('/api/data/export-fields');
    
    logger.debug('Export fields metadata retrieved', { fieldCount: response.data?.length || 0 });
    return response;
  }

  // Generic HTTP methods for admin pages
  async get<T = any>(endpoint: string, options?: { params?: Record<string, any> }): Promise<ApiResponse<T>> {
    let url = endpoint;
    if (options?.params) {
      const queryString = buildQueryString(options.params);
      url = `${endpoint}${queryString ? `?${queryString}` : ''}`;
    }
    return this.request<T>(url, { method: 'GET' });
  }

  async post<T = any>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T = any>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T = any>(endpoint: string, options?: { data?: any }): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
      body: options?.data ? JSON.stringify(options.data) : undefined,
    });
  }
}

// Create and export API client instance with environment-based URL
const getApiBaseUrl = (): string => {
  // V5: Use Vite environment variable - must be available in production
  return (import.meta as any).env.VITE_API_BASE_URL;
};

export const api = new ApiClient(getApiBaseUrl());

// Export utility functions
export const buildQueryString = (params: Record<string, any>): string => {
  const searchParams = new URLSearchParams();
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      if (Array.isArray(value)) {
        value.forEach(v => searchParams.append(key, String(v)));
      } else {
        searchParams.append(key, String(value));
      }
    }
  });
  
  return searchParams.toString();
};

export const handleApiError = (error: any): string => {
  if (error?.message) {
    return error.message;
  }
  
  if (typeof error === 'string') {
    return error;
  }
  
  return 'An unexpected error occurred';
};
